import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Typography, 
  Card, 
  Row, 
  Col, 
  Button, 
  Avatar, 
  Tag, 
  Space, 
  Divider, 
  Modal, 
  Form, 
  Input, 
  Badge,
  Skeleton
} from 'antd';
import { 
  CalendarOutlined, 
  UserOutlined, 
  EnvironmentOutlined, 
  GlobalOutlined,
  TeamOutlined,
  ArrowLeftOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { BootcampDetailResponse, BootcampApplicationRequest } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { bootcampQueryKeys } from '../../lib/queryClient';
import { isSubscriptionUiActive } from '../../utils/subscriptionUiActive';
import OptimizedImage from '../../components/Common/OptimizedImage';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const BootcampDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuth();
  const isCompany = user?.role === 'Company';
  const bootcampId = id ? parseInt(id) : null;
  
  const isSubscriptionActive = isSubscriptionUiActive(user);
  
  const [applicationModalVisible, setApplicationModalVisible] = useState(false);
  const [form] = Form.useForm();
  const toast = useToast();

  const { data: bootcamp, isLoading: loading, isError } = useQuery({
    queryKey: bootcampQueryKeys.detail(bootcampId!),
    queryFn: async () => {
      const response = await apiService.getBootcampDetail(bootcampId!);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    enabled: !!bootcampId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: hasApplied = false } = useQuery({
    queryKey: bootcampQueryKeys.applicationStatus(bootcampId!),
    queryFn: async () => {
      const response = await apiService.checkBootcampApplication(bootcampId!);
      if (!response.success) return false;
      return !!response.data;
    },
    enabled: !!bootcampId && isAuthenticated,
    staleTime: 1000 * 60,
  });

  const { data: meetingLink } = useQuery({
    queryKey: [...bootcampQueryKeys.detail(bootcampId!), 'meeting-link'],
    queryFn: async () => {
      const response = await apiService.getBootcampMeetingLink(bootcampId!);
      if (!response.success) return null;
      return response.data ?? null;
    },
    enabled: !!bootcampId && isAuthenticated && user?.role === 'Mentee',
    staleTime: 1000 * 60,
  });

  const applicationMutation = useMutation({
    mutationFn: async (values: BootcampApplicationRequest) => {
      const response = await apiService.applyToBootcamp(bootcampId!, values);
      if (!response.success) throw new Error(response.message);
      return response;
    },
    onSuccess: () => {
      setApplicationModalVisible(false);
      form.resetFields();
      if (bootcampId) queryClient.invalidateQueries({ queryKey: bootcampQueryKeys.applicationStatus(bootcampId) });
      toast.success('Başvurunuz başarıyla gönderildi!');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Başvuru gönderilirken hata oluştu');
    }
  });

  const handleApplication = (values: BootcampApplicationRequest) => {
    if (user?.role === 'Mentor') {
      toast.warning('Mentorlar bootcamp\'e katılamaz.');
      return;
    }
    applicationMutation.mutate(values);
  };

  const isFullyBooked = bootcamp ? bootcamp.currentParticipants >= bootcamp.maxParticipants : false;
  
  const bootcampImageUrl = useMemo(() => {
    return bootcamp ? apiService.getBootcampImageUrl(bootcamp.id) : null;
  }, [bootcamp?.id]);

  const mentorImageUrl = useMemo(() => {
    return bootcamp ? apiService.getMentorProfileImageUrl(bootcamp.mentorId) : null;
  }, [bootcamp?.mentorId]);

  if (isError) {
    toast.error('Bootcamp bulunamadı');
    navigate('/events');
    return null;
  }

  if (loading && !bootcamp) {
    return (
      <div className="app-page">
        <div className="app-container">
        <div style={{ width: '100%', marginBottom: '24px' }}>
          <Skeleton active paragraph={{ rows: 2, width: ['100%', '90%'] }} title={{ width: '70%' }} />
        </div>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <div className="skeleton-image-wrapper" style={{ height: '400px', width: '100%', marginBottom: '24px' }}>
              <div className="skeleton-image-fill" />
            </div>
            <div style={{ width: '100%' }}>
              <Skeleton active paragraph={{ rows: 6, width: ['100%', '95%', '90%'] }} title={false} />
            </div>
          </Col>
          <Col xs={24} lg={8}>
            <div style={{ width: '100%' }}>
              <Skeleton active paragraph={{ rows: 4, width: ['100%', '95%', '90%'] }} title={false} />
            </div>
          </Col>
        </Row>
        </div>
      </div>
    );
  }

  if (!bootcamp && !loading) {
    return (
      <div className="app-page">
        <div className="app-empty-card">
          <Text type="secondary">Bootcamp bulunamadı</Text>
        </div>
      </div>
    );
  }

  if (!bootcamp) return null;

  return (
    <div className="app-page">
      <div className="app-container">
      {/* Back Button */}
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate('/events')}
        style={{ marginBottom: '24px' }}
        aria-label="Etkinlikler sayfasına geri dön"
      >
        Geri Dön
      </Button>

      <Row gutter={[24, 24]}>
        {/* Left Column - Main Content */}
        <Col xs={24} lg={16}>
          {/* Bootcamp Image */}
          <Card className="app-content-card" style={{ marginBottom: '24px', padding: 0 }}>
            <OptimizedImage
              src={bootcampImageUrl || undefined}
              alt={`${bootcamp.title} bootcamp detay görseli`}
              width="100%"
              height="400px"
              objectFit="cover"
              loading="lazy"
              priority={true}
              placeholder="blur"
              aria-label={`${bootcamp.title} bootcamp detay görseli`}
            />
          </Card>

          {/* Bootcamp Info */}
          <Card className="app-content-card" style={{ marginBottom: '24px' }}>
            <Title level={2} style={{ marginBottom: '16px' }}>
              {bootcamp.title}
            </Title>

            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={12} sm={6}>
                <Space>
                  <CalendarOutlined style={{ color: 'var(--primary-500)' }} />
                  <Text strong>Başlangıç</Text>
                </Space>
                <div>
                  <Text>{dayjs(bootcamp.startDate).format('DD MMMM YYYY')}</Text>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <Space>
                  <CalendarOutlined style={{ color: 'var(--success)' }} />
                  <Text strong>Bitiş</Text>
                </Space>
                <div>
                  <Text>{dayjs(bootcamp.endDate).format('DD MMMM YYYY')}</Text>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <Space>
                  {bootcamp.isOnline ? <GlobalOutlined style={{ color: '#722ed1' }} /> : <EnvironmentOutlined style={{ color: '#fa8c16' }} />}
                  <Text strong>Lokasyon</Text>
                </Space>
                <div>
                  <Text>{bootcamp.isOnline ? 'Online' : bootcamp.location}</Text>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <Space>
                  <TeamOutlined style={{ color: '#13c2c2' }} />
                  <Text strong>Katılımcı</Text>
                </Space>
                <div>
                  <Text>{bootcamp.currentParticipants}/{bootcamp.maxParticipants}</Text>
                </div>
              </Col>
            </Row>

            <Divider />

            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>Açıklama</Title>
              <div 
                style={{ fontSize: '16px', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: bootcamp.description }}
              />
            </div>

            {bootcamp.requirements && (
              <div style={{ marginBottom: '24px' }}>
                <Title level={4}>Gereksinimler</Title>
                <div 
                  style={{ fontSize: '16px', lineHeight: '1.6' }}
                  dangerouslySetInnerHTML={{ __html: bootcamp.requirements }}
                />
              </div>
            )}

            {meetingLink && (
              <div style={{ marginBottom: '24px' }}>
                <Title level={4}>Toplantı Linki</Title>
                <Button 
                  type="link" 
                  href={meetingLink}
                  target="_blank"
                  icon={<GlobalOutlined />}
                >
                  Toplantıya Katıl
                </Button>
              </div>
            )}
          </Card>
        </Col>

        {/* Right Column - Sidebar */}
        <Col xs={24} lg={8}>
          {/* Price and Application */}
          <Card className="app-content-card" style={{ marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Text type="secondary" style={{ fontSize: '16px' }}>Fiyat</Text>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--success)' }}>
                ₺{bootcamp.price.toLocaleString()}
              </div>
            </div>

            {isCompany ? null : (
              <>
                {!isAuthenticated ? (
                  <Button 
                    type="primary" 
                    size="large"
                    block
                    onClick={() => navigate('/login')}
                    style={{ marginBottom: '16px' }}
                  >
                    Giriş Yap ve Başvuru Yap
                  </Button>
                ) : !isSubscriptionActive ? (
                  <div style={{ textAlign: 'center' }}>
                    <Text type="warning" style={{ display: 'block', marginBottom: '16px', fontSize: '14px' }}>
                      Başvurabilmeniz için abone olmalısınız
                    </Text>
                    <Button 
                      type="primary" 
                      size="large"
                      block
                      icon={<CrownOutlined />}
                      onClick={() => {
                        const panelPath = user?.role === 'Mentor' ? '/profile?tab=subscription' : 
                                         user?.role === 'Mentee' ? '/mentee-profile?tab=subscription' : 
                                         '/login';
                        navigate(panelPath);
                      }}
                    >
                      Abonelik Satın Al
                    </Button>
                  </div>
                ) : hasApplied ? (
                  <Button 
                    type="default" 
                    size="large"
                    block
                    disabled
                    style={{ marginBottom: '16px' }}
                  >
                    Zaten Başvuru Yaptınız
                  </Button>
                ) : user?.role === 'Mentor' ? null : (
                  <Button 
                    type="primary" 
                    size="large"
                    block
                    disabled={isFullyBooked}
                    onClick={() => setApplicationModalVisible(true)}
                    style={{ marginBottom: '16px' }}
                  >
                    {isFullyBooked ? 'Dolu' : 'Başvuru Yap'}
                  </Button>
                )}

                {isFullyBooked && isSubscriptionActive && (
                  <Text type="danger" style={{ textAlign: 'center', display: 'block' }}>
                    Bu bootcamp dolu
                  </Text>
                )}
              </>
            )}
          </Card>

          {/* Mentor Info */}
          <Card className="app-content-card" style={{ marginBottom: '24px' }}>
            <Title level={4} style={{ marginBottom: '16px' }}>Eğitmen</Title>
            
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <Avatar 
                size={80} 
                src={mentorImageUrl || undefined}
                alt={`${bootcamp.mentorName} mentor profil resmi`}
                icon={<UserOutlined />}
                style={{ marginBottom: '12px' }}
                onError={() => true}
              />
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  {bootcamp.mentorName}
                </Title>
                {bootcamp.mentorTitle && (
                  <Text type="secondary">{bootcamp.mentorTitle}</Text>
                )}
              </div>
            </div>

            {bootcamp.mentorBio && (
              <div 
                style={{ fontSize: '14px', textAlign: 'center' }}
                dangerouslySetInnerHTML={{ __html: bootcamp.mentorBio }}
              />
            )}

            {bootcamp.mentorSkills.length > 0 && (
              <div>
                <Text strong style={{ fontSize: '14px' }}>Uzmanlık Alanları:</Text>
                <div style={{ marginTop: '8px' }}>
                  <Space wrap>
                    {bootcamp.mentorSkills.map((skill, index) => (
                      <Tag key={index} color="blue">{skill}</Tag>
                    ))}
                  </Space>
                </div>
              </div>
            )}

            <Button 
              type="default" 
              block 
              onClick={() => navigate(`/mentor/${bootcamp.mentorId}`)}
              style={{ marginTop: '16px' }}
            >
              Profili Görüntüle
            </Button>
          </Card>

          {/* Quick Info */}
          <Card className="app-content-card">
            <Title level={5} style={{ marginBottom: '16px' }}>Hızlı Bilgiler</Title>
            
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Süre:</Text>
                <Text>{dayjs(bootcamp.endDate).diff(dayjs(bootcamp.startDate), 'day')} gün</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Tür:</Text>
                <Text>{bootcamp.isOnline ? 'Online' : 'Yüz Yüze'}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Kalan Kontenjan:</Text>
                <Badge 
                  count={bootcamp.maxParticipants - bootcamp.currentParticipants} 
                  style={{ backgroundColor: 'var(--success)' }}
                />
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Application Modal */}
      <Modal
        title="Bootcamp Başvurusu"
        open={applicationModalVisible}
        onCancel={() => setApplicationModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleApplication}
        >
          <Form.Item
            name="coverLetter"
            label="Başvuru Mektubu"
            rules={[
              { max: 1000, message: 'Başvuru mektubu en fazla 1000 karakter olabilir' }
            ]}
          >
            <TextArea 
              rows={6} 
              placeholder="Neden bu bootcamp'e katılmak istediğinizi açıklayın... (İsteğe bağlı)"
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setApplicationModalVisible(false)}>
                İptal
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={applicationMutation.isPending}
              >
                Başvuru Gönder
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      </div>
    </div>
  );
};

export default BootcampDetail;
